<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import InterviewStrategySection from './InterviewStrategySection.vue';
import InterviewSchedulingStartForm from '@/components/Interview/InterviewSchedulingStartForm.vue';
import MeService from '@/core/shared/me/me.service';
import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import InterviewSchedulingService from '@/core/shared/interview-scheduling/interview-scheduling.service';
import type { InterviewSchedulingRequestDto } from '@/core/shared/interview-scheduling/types/interview-scheduling.type';
import type { ProjectInterviewSettings } from '@/core/jobs/copilot-activation/types/interview-scheduling-settings.type';
import { getUserName } from '@/core/shared/user-profile/utils';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import InterviewSchedulingScorePicker from './InterviewSchedulingScorePicker.vue';
import { PolicyEnforcement } from '@factoryfixinc/ats-interfaces';

defineProps<{
  interviewSchedulingPolicy?: PolicyEnforcement;
}>();

const meService = new MeService();
const copilotActService = new CopilotActivationService();
const interviewSchedulingService = new InterviewSchedulingService();

const recruiterInfo = computed(() => ({
  email: meService.userProfile?.email || '',
  name: getUserName(meService.userProfile) || '',
  userProfileId: meService.userProfile?.id || null,
}));

const initialData = ref<ProjectInterviewSettings | null>(null);
const isEnabled = computed(() => copilotActService.interviewSettingsEnabled);
const scoreThreshold = computed(() => copilotActService.interviewSettingsScoreThreshold);

function handleSchedulingDataChange(data: InterviewSchedulingRequestDto) {
  const updatedSettings: ProjectInterviewSettings = {
    enabled: isEnabled.value,
    scoreThreshold: scoreThreshold.value,
    contacts: data.contacts,
    duration: data.duration,
    type: data.type,
    venueDetails: data.venueDetails || '',
    additionalDetails: data.additionalDetails || '',
    schedulingPreferences: data.schedulingPreferences || '',
    requireFinalConfirmation: data.requireFinalConfirmation ?? true,
    recruiterEmail: recruiterInfo.value.email,
    recruiterName: recruiterInfo.value.name,
    recruiterUserProfileId: recruiterInfo.value.userProfileId,
    customMeetingLink: data.customMeetingLink,
  };

  copilotActService.interviewSettings = updatedSettings;
}

async function handleEnabledChange(value: boolean) {
  copilotActService.interviewSettingsEnabled = value;
  if (!value) {
    interviewSchedulingService.resetValidationErrors();
  }
  if (value && !copilotActService.interviewSettings) {
    await loadOrInitializeSettings();
  }
}

function handleScoreThresholdChange(value: number) {
  copilotActService.interviewSettingsScoreThreshold = value;
}

const isLoadingSettings = ref(false);

async function loadOrInitializeSettings() {
  const projectId = copilotActService.selectedProjectId;

  if (!projectId) {
    if (!copilotActService.interviewSettings) {
      copilotActService.interviewSettings = copilotActService.getDefaultInterviewSettings();
    }
    return;
  }

  isLoadingSettings.value = true;

  const defaultSettings = copilotActService.getDefaultInterviewSettings();
  copilotActService.interviewSettings = defaultSettings;
  try {
    const interviewSettings = await interviewSchedulingService.fetchInterviewSettings(projectId);

    copilotActService.interviewSettings = interviewSettings ?? defaultSettings;
  } catch (error) {
    SnackbarService.critical('Failed to load interview automation settings');
    ErrorService.captureException(error);
  } finally {
    isLoadingSettings.value = false;
    initialData.value = copilotActService.interviewSettings;
  }
}

watch(
  () => copilotActService.selectedProjectId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      loadOrInitializeSettings();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="space-y-6">
    <div v-if="isLoadingSettings" class="flex items-center justify-center py-8">
      <v-progress-circular indeterminate color="primary" size="40" />
    </div>

    <template v-else>
      <!-- Interview Strategy Toggle -->
      <InterviewStrategySection
        :enabled="isEnabled"
        :interview-scheduling-policy="interviewSchedulingPolicy"
        @change-enabled="handleEnabledChange"
      />

      <div v-if="isEnabled">
        <InterviewSchedulingScorePicker
          :initial-score-threshold="scoreThreshold"
          @change-score-threshold="handleScoreThresholdChange"
          class="mb-4"
        />

        <InterviewSchedulingStartForm
          :initial-data="initialData ?? copilotActService.getDefaultInterviewSettings()"
          @dataChange="handleSchedulingDataChange"
        />
      </div>
    </template>
  </div>
</template>
